import React, { useState, useMemo, useCallback } from "react";
import { Button, Box, Text, useToast } from "@chakra-ui/react";
import PageHeader from "components/text/PageHeader";
import IssbContainer from "pages/ISSB/IssbContainer";
import { useParams } from "react-router-dom";
import { organizationService } from "api/organization-administration";
import { useQuery } from "react-query";
import { esgAdminKeys } from "hooks/query/esg-admin/esg-admin";
import { useAuth } from "context";
import MultiSelect from "components/MultiSelect/MultiSelect";
import templatePackageService from "api/issb/templatepackage.service";
import useAirUserRoles from "utils/AuthUtils";
import LocalSpinner from "components/LocalSpinner";
import { useRerender } from "rerenderer";
import PropInput from "components/PropInput";
import { LabelText } from "components/text/TextTypes";
import DimensionsEditorPanel from "./DimensionsEditorPanel";

export default function OrganizationPage() {
  const { id } = useParams();
  const { isOrgAdmin, isESGAdmin } = useAirUserRoles();
  const { getAccessTokenSilently } = useAuth();
  const toast = useToast();

  const { rerender } = useRerender();

  const [localOrg, setLocalOrg] = useState();
  const [isModified, setIsModified] = useState(false);

  // Fetch organization data
  const { data: org, isLoading } = useQuery({
    queryKey: esgAdminKeys.orgs().concat(id),
    queryFn: async () => {
      await getAccessTokenSilently();
      return organizationService.get(id);
    },
    enabled: !!id,
    onSuccess: (data) => {
      if (!data.settings) {
        // add a default settings object if it doesn't exist, for PropInput to edit
        data.settings = {};
      }
      setLocalOrg(data);
      setIsModified(false);
    },
  });

  // Fetch frameworks
  const { data: frameworks, isFetching } = useQuery({
    queryKey: "templatePackageService",
    queryFn: async () => {
      await getAccessTokenSilently();
      return templatePackageService.list();
    },
    enabled: !!id,
  });

  // Memoize framework tags
  const frameworkTags = useMemo(() => {
    const tagsSet = new Set();
    frameworks?.content?.forEach((fw) => fw.tags?.forEach((tag) => tagsSet.add(tag)));
    org?.accessTags?.forEach((tag) => tagsSet.add(tag));
    return [...tagsSet].map((tag) => ({ label: tag, value: tag })).sort((a, b) => a.label.localeCompare(b.label));
  }, [frameworks, org]);

  // Handle tag changes
  const handleChange = useCallback((selectedOptions) => {
    setLocalOrg((prev) => ({
      ...prev,
      accessTags: selectedOptions.map((option) => option.value),
    }));
    setIsModified(true);
  }, []);

  // Save handler
  const handleSave = useCallback(() => {
    if (!localOrg) return;
    organizationService
      .update(id, localOrg)
      .then((data) => {
        setLocalOrg(data);
        setIsModified(false);
        toast({
          title: "Organization updated",
          description: `Organization ${data.name} has been updated successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          title: "Error updating organization",
          description: error?.message || "An error occurred while updating the organization.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }, [id, localOrg, toast]);

  return (
    <IssbContainer>
      <PageHeader title={id ? `Edit Organization ${org?.name ?? ""}` : "Create Organization"} />
      <Text>ID: {id}</Text>
      <Box>
        <Text>You are {isOrgAdmin ? "" : "NOT "}an organization admin.</Text>
        <Text>
          You are {isESGAdmin ? "" : "NOT "}an ESG Administrator.
          {!isESGAdmin && " The frameworks you can see are limited to those you have access to."}
        </Text>
      </Box>
      {isFetching ? (
        <LocalSpinner />
      ) : (
        <>
          <LabelText>Framework Access Tags (leave blank for all)</LabelText>
          <MultiSelect
            value={localOrg?.accessTags?.map((tag) => ({ label: tag, value: tag })) ?? []}
            onChange={handleChange}
            options={frameworkTags}
          />

          <Text>Module (feature) access: Use the Edit Modules dialog from the organization-management page.</Text>
		  {/* TODO move that here instead */}

          <PropInput
            label="Organization Logo URL"
            type="text"
            help="Write the url for the logo of the organization. e.g. https://abcd.com/my_image.jpeg or any other valid url"
            prop="logoUrl"
            item={localOrg}
            onChange={(e) => {
              setIsModified(true);
              rerender();
            }}
          />

          <LabelText>Extra Settings</LabelText>
          <PropInput
            label="AI: Custom Prompt Snippet"
            type="textarea"
            help="Optional AI customisation - inserted into the main prompt. Can be used for e.g. tone of voice"
            prop="aiCustomPromptSnippet"
            item={localOrg?.settings}
            onChange={(e) => {
              setIsModified(true);
              rerender();
            }}
          />
          <Button mt={4} variant="primary" isLoading={isLoading} disabled={!isModified} onClick={handleSave}>
            Save
          </Button>
		  
		  <DimensionsEditorPanel org={localOrg} />
        </>		
      )}
    </IssbContainer>
  );
}
